import LinkedInButton from '@/components/buttons/LinkedInButton'
import theme from '@/components/themes/mainTheme'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import MenuIcon from '@mui/icons-material/Menu'
import YouTubeIcon from '@mui/icons-material/YouTube'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { Outlet, useNavigate } from 'react-router-dom'

const pages = [
  {
    label: 'Hem',
    href: '',
  },
  {
    label: 'Om oss & vår musik',
    href: 'about',
  },
  {
    label: 'Börja spela med oss',
    href: 'play-with-us',
  },
  {
    label: 'Kommande events',
    href: 'upcomming-events',
  },
  {
    label: 'Historia',
    href: 'history',
  },
  {
    label: 'Bild- & filmarkiv',
    href: 'gallery-selector',
  },
  {
    label: 'Kontakt',
    href: 'contact',
  },
  {
    label: 'Gästbok',
    href: 'guest-book',
  },
  {
    label: 'För våra musikanter',
    href: 'members',
  },
  {
    label: 'Länkar och sponsorer',
    href: 'links',
  },
]

const transitionTime = '0.2s'

const Baselayout: React.FC = () => {
  const navigate = useNavigate()
  const [anchorElNav, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [shrink, setShrink] = React.useState(false)

  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.values.md}px)`,
  })

  React.useEffect(() => {
    const handleScroll = () => {
      setShrink(window.scrollY > 50) // Shrink when scrolled 50px down
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          color: '#FFFFFF',
          transition: 'height 0.3s ease-in-out',
          height: shrink
            ? isMobile
              ? '65px'
              : '70px'
            : isMobile
              ? '100px'
              : '140px',
        }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          textAlign="center"
        >
          <Grid size={3}>
            <IconButton
              aria-label="Menu"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              color="inherit"
            >
              <MenuIcon
                sx={{
                  transition: [
                    `height ${transitionTime} ease-in-out`,
                    `width ${transitionTime} ease-in-out`,
                  ],
                  height: shrink ? '1em' : '1.5em',
                  width: shrink ? '1em' : '1.5em',
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={() => setAnchorEl(null)}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.label}
                  onClick={() => {
                    setAnchorEl(null)
                    navigate(`/${page.href}`)
                  }}
                >
                  <Typography sx={{ textAlign: 'center' }}>
                    {page.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Grid>
          <Grid
            size={6}
            justifyContent="space-between"
            alignItems="center"
            textAlign="center"
          >
            <Typography
              component="a"
              href="/"
              sx={{
                mr: 2,
                flexGrow: 1,
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
                textAlign: 'center',
                margin: 0,
                transition: `font-size ${transitionTime} ease-in-out`,
                fontSize: shrink
                  ? isMobile
                    ? '0.8em'
                    : '1.5em'
                  : isMobile
                    ? '1.2em'
                    : '2em',
              }}
            >
              {isMobile ? (
                <span>
                  KARLSHAMNS
                  <br />
                  SCOUTMUSIKKÅR
                </span>
              ) : (
                <span>KARLSHAMNS SCOUTMUSIKKÅR</span>
              )}
            </Typography>
          </Grid>
          <Grid size={3}>
            <img
              src={'/images/logos/scouterna_logo_round_small.png'}
              alt="scouternas-logo"
              style={{
                margin: '10px',
                transition: `height ${transitionTime} ease-in-out`,
                height: shrink
                  ? isMobile
                    ? '40px'
                    : '50px'
                  : isMobile
                    ? '80px'
                    : '120px',
              }}
            />
          </Grid>
        </Grid>
      </AppBar>
      <Box
        component="main"
        sx={{
          transition: 'padding-top 0.3s ease-in-out',
          paddingTop: shrink
            ? isMobile
              ? '65px'
              : '70px'
            : isMobile
              ? '100px'
              : '140px',
        }}
      >
        <Outlet context={{ isDrawerOpen: false }} />
      </Box>
      <AppBar
        position="static"
        color="primary"
        sx={{ top: 'auto', bottom: 0, padding: '15px', color: '#FFFFFF' }}
      >
        <Grid container direction="row" justifyContent="space-between">
          <Grid>
            <LinkedInButton />
          </Grid>
          <Grid>
            <IconButton
              aria-label="Instagram"
              sx={{ color: '#FFFFFF' }}
              onClick={() =>
                window.open('https://www.instagram.com/scoutmusikkaren/')
              }
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              aria-label="Facebook"
              sx={{ color: '#FFFFFF' }}
              onClick={() =>
                window.open('https://www.facebook.com/scoutmusikkaren/')
              }
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              aria-label="Youtube"
              sx={{ color: '#FFFFFF' }}
              onClick={() =>
                window.open(
                  'https://www.youtube.com/@KarlshamnsScoutmusikk%C3%A5r',
                )
              }
            >
              <YouTubeIcon />
            </IconButton>
          </Grid>
        </Grid>
      </AppBar>
    </>
  )
}
export default Baselayout
